/**
 * Where the puck identity on the dock came from, and the rule for what may
 * overwrite it.
 *
 * "This" and "here" in a spoken command resolve against one piece of state:
 * `store.state.voiceScreenContext`. Until now only PuckSection wrote it, so the
 * open puck was the only possible answer. Once a QR scan (Stage 2) or a pair of
 * glasses (Stage 4) can also say what the user is looking at, two writers race:
 * scrolling the page, closing a puck or navigating a route all fire a `screen`
 * publish, and any of them would silently throw away a scan the user made two
 * seconds ago. The failure is invisible — the chip keeps showing a puck, just
 * the wrong one — and the command applies to it.
 *
 * Hence the age rule below: a `screen` publish is the weakest kind of evidence
 * (nobody pointed at anything, a component merely mounted), so it yields to a
 * deliberate act — a scan, a tap, a glance — for FRESH_MS. A deliberate act
 * always wins immediately; the user pointing at something is never wrong.
 *
 * Pure ESM: no store, no DOM, no timers. `scripts/test-voice-context.js` runs
 * this module directly.
 */

/** Every source the chip knows how to name. Order is oldest-shipped first. */
export const SOURCES = ["screen", "tapped", "camera", "glasses"];

/** What each source is called in the chip. User-facing, not identifiers. */
export const SOURCE_COPY = {
  screen: "open on screen",
  tapped: "tapped",
  camera: "phone camera",
  glasses: "glasses camera",
};

/**
 * How long a deliberate context outranks a screen publish.
 *
 * 60 s is a command's working life: long enough to scan a puck, walk to the
 * bench and say the sentence, short enough that a scan from a previous task
 * does not still be governing what "this" means.
 */
export const FRESH_MS = 60000;

/** A source we do not recognise is treated as the weakest one, not trusted. */
function normalizeSource(source) {
  return SOURCES.indexOf(source) === -1 ? "screen" : source;
}

/** Empty string, 0 and undefined all mean "nothing", not a label of their own. */
function normalizeLabel(value) {
  if (value === null || value === undefined || value === "") return null;
  return String(value);
}

/**
 * Full context shape, whatever partial thing came in.
 *
 * `at` is left to the caller to supply because the two callers want different
 * things: a new publish is stamped `now`, while a context that survives a merge
 * must keep its ORIGINAL stamp — restamping it would refresh the freshness
 * window on every screen publish, and a scan would then outrank the screen for
 * the rest of the session instead of for a minute.
 */
function normalizeContext(ctx, at) {
  return {
    puckLabel: normalizeLabel(ctx && ctx.puckLabel),
    puckId: normalizeLabel(ctx && ctx.puckId),
    boxLabels:
      ctx && Array.isArray(ctx.boxLabels) ? ctx.boxLabels.map(String) : [],
    source: normalizeSource(ctx && ctx.source),
    at,
  };
}

/** A stamp we cannot read is treated as ancient, so it never blocks a publish. */
function stampOf(ctx, fallback) {
  const at = ctx && ctx.at;
  return typeof at === "number" && Number.isFinite(at) ? at : fallback;
}

/**
 * The chip's one line: what the dock believes "this" means, and why it believes
 * it. The source half is not decoration — it is how a user notices that the
 * dock is looking at the puck they scanned rather than the one on screen.
 */
export function chipCopy(ctx) {
  const label = normalizeLabel(ctx && ctx.puckLabel);
  if (!label) return "Looking at nothing — open a puck or scan one";
  return "Looking at " + label + " · " + SOURCE_COPY[normalizeSource(ctx && ctx.source)];
}

/**
 * The line under the chip. With a puck it teaches the shortcut; without one it
 * says the only thing that will work, because "move this" with no context is a
 * refusal the user would otherwise have to earn by trying it.
 */
export function hintCopy(ctx) {
  if (!normalizeLabel(ctx && ctx.puckLabel)) {
    return "Say the puck name in the command.";
  }
  return (
    "'This' and 'here' mean the puck you're looking at. " +
    "Say a puck name only if it's somewhere else."
  );
}

/**
 * Merge a new context over the current one.
 *
 * `next` wins, with one exception: a `screen` publish does not replace a
 * camera, tapped or glasses context younger than FRESH_MS. That exception
 * covers the screen CLEAR too (PuckSection unmounting with `puckLabel: null`),
 * which is the case that actually bites — closing a puck after scanning one
 * would otherwise blank a context the user just created deliberately.
 *
 * Returns a full `{ puckLabel, puckId, boxLabels, source, at }` either way.
 */
export function mergeContext(prev, next, now = Date.now()) {
  // Nothing offered: hand back what is already there, in the full shape.
  if (!next) return normalizeContext(prev, stampOf(prev, now));

  const nextSource = normalizeSource(next.source);
  if (nextSource === "screen") {
    const prevSource = normalizeSource(prev && prev.source);
    const prevAt = stampOf(prev, -Infinity);
    const prevIsDeliberate = prevSource !== "screen";
    const prevIsFresh = now - prevAt < FRESH_MS;
    if (prevIsDeliberate && prevIsFresh) {
      // Keep the deliberate context, stamp and all. See normalizeContext.
      return normalizeContext(prev, prevAt);
    }
  }

  return normalizeContext(next, stampOf(next, now));
}
